import request from '@teetsh/app/src/utils/request';
import useNotification from '@teetsh/app/src/containers/App/Notifications/useNotification';
import { useSchoolSchoolyear } from '@teetsh/app/src/containers/App/context/SchoolyearSchoolProvider';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { LivretEval } from '@teetsh/app/src/containers/Livrets/types/livret';
import livretKeys from '@teetsh/app/src/containers/Livrets/queries/keys';
import { updateMatiereLivretEleve } from '@teetsh/app/src/containers/Livrets/queries/updateMatiereLivretEleve';
import { createCompetence } from '@teetsh/app/src/containers/Livrets/queries/competences/createCompetence';
import { useTranslation } from 'react-i18next';
import { useLSUErrorsMapping } from 'containers/Livrets/useLSUErrorsMapping';

export interface CreateLivretEleveCompetenceDTO {
  id: string;
  matiereId: string;
  domaineId?: string;
  name: string;
  note?: number;
}

function createLivretEleveCompetence({
  schoolyearId,
  schoolId,
  livretEleveId,
  competence,
}: {
  schoolyearId: string;
  schoolId: string;
  livretEleveId: string;
  competence: CreateLivretEleveCompetenceDTO;
}) {
  const requestURL = `/schoolyears/${schoolyearId}/schools/${schoolId}/livrets-eleves/${livretEleveId}/competences`;
  return request(requestURL, {
    method: 'POST',
    body: competence,
  });
}

interface Props {
  livretEleveId: string;
  livretEvalId: string;
}

export function useCreateLivretCompetence(props: Props) {
  const { notifyError } = useNotification();
  const errorsMapping = useLSUErrorsMapping();
  const { schoolyear, school } = useSchoolSchoolyear();
  const { livretEleveId, livretEvalId } = props;
  const queryClient = useQueryClient();
  const { t } = useTranslation(['carnet']);
  const { mutateAsync } = useMutation<
    LivretEval,
    any,
    CreateLivretEleveCompetenceDTO
  >({
    mutationFn: (competence: CreateLivretEleveCompetenceDTO) =>
      createLivretEleveCompetence({
        schoolyearId: schoolyear.id,
        schoolId: school.id,
        livretEleveId,
        competence,
      }),
    onMutate: async (competence: CreateLivretEleveCompetenceDTO) => {
      await queryClient.cancelQueries({
        queryKey: livretKeys.livretDetail(
          schoolyear.id,
          school.id,
          livretEvalId,
        ),
      });
      const previousLivretEval = queryClient.getQueryData(
        livretKeys.livretDetail(schoolyear.id, school.id, livretEvalId),
      );
      queryClient.setQueryData<LivretEval>(
        livretKeys.livretDetail(schoolyear.id, school.id, livretEvalId),
        (oldLivretEval: LivretEval) => ({
          ...oldLivretEval,
          livretsEleves: (oldLivretEval?.livretsEleves || []).map(
            livretEleve => {
              if (livretEleve.id === livretEleveId) {
                const matiere = livretEleve.matieres.find(
                  m => m.id === competence.matiereId,
                );
                if (!matiere) return livretEleve;
                const domaine = competence?.domaineId
                  ? matiere.domaines.find(d => d.id === competence.domaineId)
                  : undefined;
                const newMatiere = createCompetence(
                  matiere,
                  {
                    name: competence.name,
                    note: competence.note,
                    id: competence.id,
                  },
                  domaine,
                );
                return updateMatiereLivretEleve(
                  livretEleve,
                  newMatiere,
                  oldLivretEval.typePositionnement,
                  errorsMapping,
                );
              }
              return livretEleve;
            },
          ),
        }),
      );
      return { previousLivretEval };
    },
    onError: () => {
      notifyError({
        title: t('carnet:messages.competence.createError'),
      });
    },
  });
  return mutateAsync;
}
